import { createTimedTracker } from '@gonfalon/analytics/createTimedTracker';
import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { ExperienceTracker } from '@gonfalon/analytics/ExperienceTracker';
import { trackWithDetails } from '@gonfalon/analytics/trackWithDetails';
import { type AnalyticEventData } from '@gonfalon/analytics/types';
import { isPlainJSONObject } from '@gonfalon/types/isJSONValue';

// Todo: once we retire the legacy flag creation, move these analytics to the new flag creation package @gonfalon/flag-creation
import type {
  FlagLifecycleSettingsEventInfo,
  FlagTargetingEventInfo,
  MetricsTrackEventInfo,
  MigrationChartTypes,
  MigrationMetricsChartDataEventInfo,
  NewFlagCreationEventInfo,
} from './types';

export const trackCreateNewFlagClicked = ({ component, type, cta, location }: AnalyticEventData) =>
  trackWithDetails({
    component,
    type,
    cta,
    event: 'Create New Flag Clicked',
    category: 'New Flag Created',
    location,
  });

export const trackFlagDashboardEvent = createTrackerForCategory('Flags Dashboard');

export const trackEmptyStateCreateFlagButtonClicked = () =>
  trackCreateNewFlagClicked({ component: 'Empty State', type: 'button', cta: 'Create flag', location: 'Empty State' });

export const trackEmptyStateLearnMoreButtonClicked = () =>
  trackFlagDashboardEvent('Flag Empty State Learn More Button Clicked', { location: 'Empty State' });

export const trackEmptyStateSandboxButtonClicked = () =>
  trackFlagDashboardEvent('Flag Empty State Sandbox Button Clicked', { location: 'Empty State' });

export const flagCreationTimers = {
  TIME_TO_CREATE: 'Time to create',
} as const;

const _trackNewFlagCreationEvent = createTimedTracker('New Flag Creation');

export const trackNewFlagCreationEvent = (event: string, attributes?: NewFlagCreationEventInfo) =>
  _trackNewFlagCreationEvent(event, attributes);

export const trackNewFlagCreationFormSubmitted = (attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('New Flag Creation Form Submitted', attributes);

export const trackNewFlagCreationViewed = (attributes?: NewFlagCreationEventInfo) =>
  _trackNewFlagCreationEvent.start(flagCreationTimers.TIME_TO_CREATE).track('New Flag Creation Viewed', attributes);

export const trackNewFlagCreationCancelButtonClicked = (attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('New Flag Creation Cancel Button Clicked', attributes);

export const trackNewFlagCreationDefaultKeyChanged = (flagKey: string, attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('New Flag Creation Default Flag Key Changed', { flagKey, ...attributes });

export const trackNewFlagCreationTemplateSelected = (template: string, attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('Flag Template Selected', { template, ...attributes });

export const trackNewFlagCreationVariationTypeChanged = (
  variationType: string,
  attributes?: NewFlagCreationEventInfo,
) => trackNewFlagCreationEvent('Flag Variation Type Changed', { variationType, ...attributes });

export const trackNewFlagCreationMaintainerAdded = (
  maintainerType: 'member' | 'team',
  attributes?: NewFlagCreationEventInfo,
) => trackNewFlagCreationEvent('Flag Maintainer Added', { maintainerType, ...attributes });

export const trackNewFlagCreationWithJiraIntegration = (attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('Flag Created With Jira Integration', { hasJiraIntegration: true, ...attributes });

export const trackNewFlagCreationTagsAdded = (tagCount: number, attributes?: NewFlagCreationEventInfo) =>
  trackNewFlagCreationEvent('Flag Tags Added', { tagCount, ...attributes });

export const trackNewFlagCreationTimeToCreate = () =>
  _trackNewFlagCreationEvent
    .measure(flagCreationTimers.TIME_TO_CREATE)
    .track('New Flag Creation Time To Create')
    .restart();

export const trackMigrationMetricsEvent = (event: string, attributes?: MetricsTrackEventInfo) =>
  createTrackerForCategory('Migration metrics')(event, attributes);

export const trackMetricsChartChanged = (
  selectedMetric: MigrationChartTypes,
  stageCount?: number,
  contextKind?: string,
) => trackMigrationMetricsEvent('Metrics Chart Changed', { selectedMetric, stageCount, contextKind });

export const trackMigrationMetricsChartDataLoaded = (attributes: MigrationMetricsChartDataEventInfo) =>
  createTrackerForCategory('Migration metrics')('Migration Metrics Chart Data Loaded', attributes);

export const trackFlagLinksEvent = createTrackerForCategory('FlagLinks');

export const trackFlagAudienceEvent = createTrackerForCategory('FlagAudience');

export const trackRuleExclusionEvent = createTrackerForCategory('RuleExclusion');

const trackFlagTargetingEvent = (event: string, attributes?: FlagTargetingEventInfo) =>
  createTrackerForCategory('FlagsTargeting')(event, attributes);

export const trackEmptyStateSegmentTargetingClicked = () =>
  trackFlagTargetingEvent('Empty State Segment Targeting Clicked');

export const trackEmptyStateMobileTargetingClicked = () =>
  trackFlagTargetingEvent('Empty State Mobile Targeting Clicked');

export const trackEmptyStateOptionClicked = (targetKind: string) =>
  trackFlagTargetingEvent('Empty State Option Clicked', { targetKind });

export const trackTargetingRuleValuesShowMoreClicked = () =>
  trackFlagTargetingEvent('Targeting Rule Values Show More Clicked');
export const trackTargetingRuleValuesShowLessClicked = () =>
  trackFlagTargetingEvent('Targeting Rule Values Show Less Clicked');

export const trackIndividualTargetValuesShowClicked = () => trackFlagTargetingEvent('Individual Values Show Clicked');
export const trackIndividualTargetValuesShowLessClicked = () =>
  trackFlagTargetingEvent('Individual Target Values Show Less Clicked');

export const trackFlagDisplayPreferencesEvent = createTrackerForCategory('Flag Display Preferences');
export const trackFlagFiltersEvent = createTrackerForCategory('Flag Filters');

// Flag lifecycle settings events
export const trackFlagLifecycleSettingsEvent = createTrackerForCategory('Flag Lifecycle Settings');
export const trackFlagLifecycleSettingsViewed = () => trackFlagLifecycleSettingsEvent('Flag Lifecycle Settings Viewed');
export const trackFlagLifecycleSettingsSaved = (attributes?: FlagLifecycleSettingsEventInfo) =>
  trackFlagLifecycleSettingsEvent('Flag Lifecycle Settings Saved', attributes);
export const trackFlagLifecycleSettingsResetToDefaultClicked = () =>
  trackFlagLifecycleSettingsEvent('Flag Lifecycle Settings Reset To Default Clicked');

export const trackReadyToArchiveModalEvent = createTrackerForCategory('Ready to Archive Modal');
export const trackReadyForCodeRemovalModalEvent = createTrackerForCategory('Ready for Code Removal Modal');
export const trackArchiveWithoutCodeRemovalModal = createTrackerForCategory('Archive Without Code Removal Modal');

// Bulk edit related events
export const trackBulkEditEvent = createTrackerForCategory('BulkEdit');
export const trackBulkEditStarted = () => trackBulkEditEvent('Bulk Edit Started');

// Export as CSV events
export const trackExportEvent = createTrackerForCategory('Export');
export const trackExportAsCSVClicked = (itemsCount: number) =>
  trackExportEvent('Export As CSV Clicked', { itemsCount });

// Clear all events
export const trackClearAllEvent = createTrackerForCategory('ClearAll');
export const trackClearAllClicked = () => trackClearAllEvent('Clear All Clicked');

// Convert to segment events
export const trackConvertToSegmentEvent = createTrackerForCategory('ConvertToSegment');
export const trackConvertToSegmentClicked = () => trackConvertToSegmentEvent('Convert To Segment Clicked');
export const trackConvertToSegmentCompleted = (segmentName: string) =>
  trackConvertToSegmentEvent('Convert To Segment Completed', { segmentName });

// Scheduled targeting events
export const trackScheduledTargetingEvent = createTrackerForCategory('ScheduledTargeting');
export const trackScheduledTargetingEnabled = () => trackScheduledTargetingEvent('Scheduled Targeting Enabled');
export const trackScheduledTargetingConfigured = (scheduleType: string) =>
  trackScheduledTargetingEvent('Scheduled Targeting Configured', { scheduleType });

// Flag settings events
export const trackFlagSettingsEvent = createTrackerForCategory('Flag Settings');
export const trackFlagNameBreadcrumbClicked = () => trackFlagSettingsEvent('Flag Name Breadcrumb Clicked');
export const trackFlagDescriptionClicked = () => trackFlagSettingsEvent('Flag Description Clicked');
export const trackFlagAdvancedControlsClicked = (control: 'clientSide' | 'mobile' | 'permanent') =>
  trackFlagSettingsEvent('Flag Advanced Controls Clicked', { control });

export type ExpiringTargetsEvent = 'Update expiration date' | 'Remove expiring target';

export const expiringTargetsTracker = ExperienceTracker.create<ExpiringTargetsEvent>('Expiring Targets', {
  owners: ['team-foundation'],
  category: 'Flags',
});

// All variation-JSON-schema events land under one category so the funnel
// (entry → authored → previewed → saved) stays joinable in dashboards. The
// `surface` attribute separates flag-creation vs flag-detail emissions.

export type FlagSchemaSurface = 'flag-create' | 'flag-detail';
export type FlagSchemaChangeMode = 'added' | 'updated' | 'removed';
export type FlagSchemaSuggestErrorCategory = 'no-variations' | 'variation-coerce-failed' | 'inference-conflict';
export type FlagSchemaAdditionalPropertiesMode = 'closed' | 'open' | 'schema' | 'other';

export type FlagSchemaSummary = {
  variationJsonSchemaBytes?: number;
  variationJsonSchemaTopLevelKind?: 'object' | 'boolean' | 'non-object';
  variationJsonSchemaTopLevelType?: string;
  variationJsonSchemaPropertyCount?: number;
  variationJsonSchemaRequiredCount?: number;
  variationJsonSchemaEnumCount?: number;
  variationJsonSchemaHasTitle?: boolean;
  variationJsonSchemaHasDescription?: boolean;
  variationJsonSchemaAdditionalPropertiesMode?: FlagSchemaAdditionalPropertiesMode;
  variationJsonSchemaDialect?: string;
  variationJsonSchemaUsesRef?: boolean;
  variationJsonSchemaBoolValue?: boolean;
};

export const trackFlagSchemaEvent = createTrackerForCategory('Flag JSON Schema');

export function summarizeFlagSchemaForAnalytics(schema: unknown): FlagSchemaSummary {
  const summary: FlagSchemaSummary = {};
  if (schema === undefined || schema === null) {
    return summary;
  }
  let parsed: unknown = schema;
  if (typeof schema === 'string') {
    summary.variationJsonSchemaBytes = schema.length;
    try {
      parsed = JSON.parse(schema);
    } catch {
      summary.variationJsonSchemaTopLevelKind = 'non-object';
      return summary;
    }
  } else {
    try {
      summary.variationJsonSchemaBytes = JSON.stringify(parsed).length;
    } catch {
      // non-stringifiable input — skip byte count
    }
  }
  if (typeof parsed === 'boolean') {
    summary.variationJsonSchemaTopLevelKind = 'boolean';
    summary.variationJsonSchemaBoolValue = parsed;
    return summary;
  }
  if (!isPlainJSONObject(parsed)) {
    summary.variationJsonSchemaTopLevelKind = 'non-object';
    return summary;
  }
  const obj = parsed;
  summary.variationJsonSchemaTopLevelKind = 'object';
  if (typeof obj.type === 'string') {
    summary.variationJsonSchemaTopLevelType = obj.type;
  }
  if (typeof obj.title === 'string' && obj.title.length > 0) {
    summary.variationJsonSchemaHasTitle = true;
  }
  if (typeof obj.description === 'string' && obj.description.length > 0) {
    summary.variationJsonSchemaHasDescription = true;
  }
  if (isPlainJSONObject(obj.properties)) {
    summary.variationJsonSchemaPropertyCount = Object.keys(obj.properties).length;
  }
  if (Array.isArray(obj.required)) {
    summary.variationJsonSchemaRequiredCount = obj.required.length;
  }
  if (Array.isArray(obj.enum)) {
    summary.variationJsonSchemaEnumCount = obj.enum.length;
  }
  if ('additionalProperties' in obj) {
    const ap = obj.additionalProperties;
    if (ap === true) {
      summary.variationJsonSchemaAdditionalPropertiesMode = 'open';
    } else if (ap === false) {
      summary.variationJsonSchemaAdditionalPropertiesMode = 'closed';
    } else if (isPlainJSONObject(ap)) {
      summary.variationJsonSchemaAdditionalPropertiesMode = 'schema';
    } else {
      summary.variationJsonSchemaAdditionalPropertiesMode = 'other';
    }
  }
  if (typeof obj.$schema === 'string' && obj.$schema.length > 0) {
    summary.variationJsonSchemaDialect = obj.$schema;
  }
  if (typeof obj.$ref === 'string') {
    summary.variationJsonSchemaUsesRef = true;
  }
  return summary;
}

// `intent` rather than `mode` so the field doesn't collide with the
// past-tense `mode` on Saved/Failed events in the warehouse.
export const trackFlagSchemaEditButtonClicked = (intent: 'add' | 'edit') =>
  trackFlagSchemaEvent('Schema Edit Button Clicked', { intent, surface: 'flag-detail' });

export const trackFlagSchemaSuggestClicked = (params: {
  surface: FlagSchemaSurface;
  outcome: 'success' | 'error';
  variationCount: number;
  errorCategory?: FlagSchemaSuggestErrorCategory;
}) => trackFlagSchemaEvent('Schema Suggest Clicked', params);

export const trackFlagSchemaPreviewClicked = (params: {
  outcome: 'valid' | 'failures' | 'error';
  failureCount?: number;
  errorCategory?: 'rest-api';
}) => trackFlagSchemaEvent('Schema Preview Clicked', { surface: 'flag-detail', ...params });

export const trackFlagSchemaSaved = (params: { mode: FlagSchemaChangeMode; schema?: unknown }) =>
  trackFlagSchemaEvent('Schema Saved', {
    mode: params.mode,
    surface: 'flag-detail',
    ...(params.mode === 'removed' ? {} : summarizeFlagSchemaForAnalytics(params.schema)),
  });

export const trackFlagSchemaSaveFailed = (params: { mode: FlagSchemaChangeMode; errorCategory?: 'rest-api' }) =>
  trackFlagSchemaEvent('Schema Save Failed', { surface: 'flag-detail', ...params });

export const trackFlagSchemaFieldOpened = (variationType: string) =>
  trackFlagSchemaEvent('Schema Field Opened', { surface: 'flag-create', variationType });

export const trackFlagSchemaFieldRemoved = (variationType: string) =>
  trackFlagSchemaEvent('Schema Field Removed', { surface: 'flag-create', variationType });
